import {
  getCountries,
  getCountryCallingCode,
  isPossiblePhoneNumber,
  parsePhoneNumberFromString,
} from "libphonenumber-js";
import { sortBy, trimStart, uniq } from "lodash";
import { TMessageKey } from "../i18n/types.gen";
import { PhoneNumber } from "../common/common";

export type CountryWithCallingCode = {
  isoCode: string;
  callingCode: string;
};

export const countriesWithCallingCode: CountryWithCallingCode[] = getCountries().map((c) => ({
  isoCode: c.toString(),
  callingCode: getCountryCallingCode(c).toString(),
}));

export function lookupCountry(countryCode: string, number: string): string | undefined {
  const candidates = countriesWithCallingCode.filter((c) => c.callingCode === countryCode);
  if (candidates.length === 1) {
    return candidates[0].isoCode;
  }
  const result = parsePhoneNumberFromString(number, { defaultCallingCode: countryCode });
  if (result && result.isPossible() && result.country) {
    return result.country;
  }
  if (candidates.length > 1) {
    return candidates[0].isoCode;
  }
}

export const countryCodes: string[] = sortBy(
  uniq(getCountries().map((c) => String(getCountryCallingCode(c)))),
);

export const defaultCountryCode = "47"; // TODO Geolocate client's IP address
export const defaultCountry: string = countriesWithCallingCode.find(
  (c) => c.callingCode === defaultCountryCode,
)!.isoCode;

export enum PhoneNumberValidationError {
  INVALID_COUNTRY_CODE = "INVALID_COUNTRY_CODE",
  INVALID_NUMBER = "INVALID_NUMBER",
}

export const phoneNumberValidationMessages: Record<PhoneNumberValidationError, TMessageKey> = {
  [PhoneNumberValidationError.INVALID_COUNTRY_CODE]: "phone-number.invalid-country-code",
  [PhoneNumberValidationError.INVALID_NUMBER]: "phone-number.invalid-number",
};

export function validatePhoneNumber(
  countryCode: string,
  number: string,
): PhoneNumberValidationError | undefined {
  if (!countryCodes.includes(countryCode)) {
    return PhoneNumberValidationError.INVALID_COUNTRY_CODE;
  }
  if (!isPossiblePhoneNumber(number, { defaultCallingCode: countryCode })) {
    return PhoneNumberValidationError.INVALID_NUMBER;
  }
}

export function parsePhoneNumber(fullNumber: string): PhoneNumber | undefined {
  const result = parsePhoneNumberFromString(fullNumber, { defaultCallingCode: defaultCountryCode });
  if (result) {
    return {
      value: result.nationalNumber.toString(),
      countryCode: result.countryCallingCode.toString(),
    };
  }
}

/**
 * @see https://en.wikipedia.org/wiki/E.164
 */
export function formatPhoneNumberToE164(phoneNumber: PhoneNumber): string {
  function stripSeparators(s: string) {
    return s.replace(/[^\d+]/g, "");
  }

  function stripPrefix(s: string) {
    return trimStart(s, "+");
  }

  const number = phoneNumber.value;
  if (number.startsWith("+")) {
    return stripSeparators(number);
  }
  const countryCodeRaw = (phoneNumber?.countryCode ?? "").trim();
  const countryCode = countryCodeRaw ? `+${stripPrefix(countryCodeRaw)}` : "";
  return stripSeparators(`${countryCode}${number}`);
}

export function formatPhoneNumber(phoneNumber: PhoneNumber | undefined): string {
  if (!phoneNumber) {
    return "";
  }
  const isoString = formatPhoneNumberToE164(phoneNumber);
  const parsed = parsePhoneNumberFromString(isoString);
  return parsed?.formatInternational() ?? isoString;
}

export function fixIncorrectlySerializedPhoneNumber(
  phoneNumber: PhoneNumber | undefined,
): PhoneNumber | undefined {
  if (phoneNumber?.value.startsWith("+")) {
    return parsePhoneNumber(phoneNumber.value);
  }
  if (phoneNumber?.countryCode?.startsWith("+")) {
    return { ...phoneNumber, countryCode: phoneNumber?.countryCode?.substring(1) };
  }
  return phoneNumber;
}
